import { useState } from 'react';
import { AlertTriangle, CheckCircle, Bell, BellOff, UserCheck } from 'lucide-react';
import { FloatingPanel } from '../map/FloatingPanel';
import { resolveAlarmDescription } from '../../data/alarmCatalog';
import { toDisplayId, toCanonicalId } from '../../utils/canonicalId';
import { useSessionAlarmMutes, isSessionMuted } from '../../store/sessionAlarmMutes';
import { useAlarmMasks, isAlarmMasked } from '../../store/alarmMasks';
import { useAlarmAcks, findAck, ACK_ROLES } from '../../store/alarmAcks';
import { useAuth } from '../../hooks/useAuth';
import { useOverridesVersion } from '../../store/overridesVersion';

// Severity mapping
type Severity = 'critical' | 'warning' | 'info';
const ALARM_SEVERITY: Record<string, Severity> = {
  C: 'critical', I: 'critical', E: 'critical',
  A: 'warning', B: 'warning', J: 'warning', L: 'warning', V: 'warning', N: 'warning',
};

const SEVERITY_COLORS: Record<Severity, string> = {
  critical: 'text-red-400',
  warning: 'text-amber-400',
  info: 'text-blue-400',
};


export interface MockAlarm {
  id: number;
  driverId: number;
  code: string;
  timestamp: number;
  resolvedAt?: number;
  /** Enmascarada (ARS-09) o silenciada en esta sesión: se lista atenuada
   * pero no cuenta como activa en contador, badges ni vista realista. */
  muted?: boolean;
}

export function isActiveAlarm(a: MockAlarm): boolean {
  return !a.resolvedAt && !a.muted;
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatAckTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

export function AlarmPanel({ alarms, highlightId }: { alarms: MockAlarm[]; highlightId?: string }) {
  useOverridesVersion(s => s.version); // re-render si se da de alta/baja un código custom (SC-43)
  const activeCount = alarms.filter(isActiveAlarm).length;
  const sessionMuted = useSessionAlarmMutes(s => s.muted);
  const muteSession = useSessionAlarmMutes(s => s.mute);
  const fixedMasks = useAlarmMasks(s => s.masks);
  const acks = useAlarmAcks(s => s.acks);
  const ackAlarm = useAlarmAcks(s => s.ack);
  // En dev (VITE_AUTH_DISABLED=true) no hay /auth/me y `user` es null — mismo
  // relax que showEditMode/showMapToggle en App.tsx; el backend decide igual.
  const isAuthDisabled = import.meta.env.VITE_AUTH_DISABLED === 'true';
  const canAck = useAuth(s => ACK_ROLES.has(s.user?.role ?? '')) || isAuthDisabled;

  // Fila desplegada con el input de nota — una sola a la vez.
  const [ackingKey, setAckingKey] = useState<string | null>(null);
  const [ackNote, setAckNote] = useState('');
  const [ackError, setAckError] = useState('');
  const [ackSaving, setAckSaving] = useState(false);

  const openAck = (key: string) => {
    setAckingKey(key);
    setAckNote('');
    setAckError('');
  };
  const closeAck = () => {
    setAckingKey(null);
    setAckError('');
  };
  const confirmAck = async (canonicalId: string, code: string) => {
    setAckSaving(true);
    setAckError('');
    const ok = await ackAlarm(canonicalId, code, ackNote.trim());
    setAckSaving(false);
    if (!ok) {
      setAckError('No se pudo reconocer — reintenta');
      return;
    }
    closeAck();
  };

  return (
    <FloatingPanel
      title={`Alarmas${activeCount > 0 ? ` (${activeCount})` : ''}`}
      position="inline"
      defaultCollapsed={false}
      highlightId={highlightId}
      icon={activeCount > 0
        ? <Bell className="w-3 h-3 text-amber-400" />
        : <BellOff className="w-3 h-3 text-gray-500" />
      }
    >
      <div className="max-h-48 overflow-y-auto min-w-[240px] sm:min-w-[320px] max-w-[440px]">
        {alarms.length === 0 ? (
          <div className="px-3 py-3 text-xs text-gray-500 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            Sin alarmas activas
          </div>
        ) : (
          alarms.map(alarm => {
            const isResolved = !!alarm.resolvedAt;
            const canonicalId = toCanonicalId(alarm.driverId);
            const fixedMasked = isAlarmMasked(fixedMasks, canonicalId, alarm.code);
            const mutedThisSession = isSessionMuted(sessionMuted, canonicalId, alarm.code);
            const isMuted = !isResolved && (fixedMasked || mutedThisSession);
            const ack = isResolved ? undefined : findAck(acks, canonicalId, alarm.code);
            const isAcked = ack !== undefined;
            // Clave por ocurrencia (incluye el timestamp): una reaparición
            // de la misma alarma no hereda un formulario abierto, y si otro
            // operador la reconoce (o se resuelve) mientras tanto, se cierra.
            const key = `${canonicalId}:${alarm.code}:${alarm.timestamp}`;
            const isAcking = ackingKey === key && !isAcked && !isResolved;
            const severity = ALARM_SEVERITY[alarm.code] || 'info';
            const description = resolveAlarmDescription(alarm.code);
            return (
              <div
                key={alarm.id}
                className={`px-3 py-1.5 border-b border-gray-700/30 last:border-0 transition-colors ${isResolved || isMuted || isAcked ? 'opacity-50' : 'hover:bg-gray-800/30'}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-500 font-mono flex-shrink-0">
                    {formatTime(alarm.timestamp)}
                  </span>
                  {isResolved
                    ? <CheckCircle className="w-3 h-3 flex-shrink-0 text-green-400" />
                    : isAcked
                      ? <UserCheck className="w-3 h-3 flex-shrink-0 text-teal-400" />
                      : isMuted
                        ? <BellOff className="w-3 h-3 flex-shrink-0 text-gray-500" />
                        : <AlertTriangle className={`w-3 h-3 flex-shrink-0 ${SEVERITY_COLORS[severity]}`} />
                  }
                  <span className={`text-[11px] font-mono font-medium ${isResolved ? 'text-green-400/70 line-through' : isMuted || isAcked ? 'text-gray-400' : 'text-white'}`}>
                    {toDisplayId(alarm.driverId)}
                  </span>
                  <span className="text-[10px] text-gray-400 font-mono">{alarm.code}</span>
                  <span
                    className={`text-[11px] truncate flex-1 min-w-0 ${isResolved ? 'text-gray-500 line-through' : isMuted || isAcked ? 'text-gray-500' : 'text-gray-300'}`}
                    title={description === alarm.code ? undefined : description}
                  >
                    {description === alarm.code ? '' : description}
                  </span>
                  {ack && (
                    <span
                      className="flex-shrink-0 text-[9px] text-teal-500/80 font-mono truncate max-w-[110px]"
                      title={`Reconocida por ${ack.ackedBy} a las ${formatAckTime(ack.ackedAt)}${ack.note ? ` — ${ack.note}` : ''}`}
                    >
                      {ack.ackedBy.split('@')[0]} · {formatAckTime(ack.ackedAt)}
                    </span>
                  )}
                  {!isResolved && !isAcked && canAck && !isAcking && (
                    <button
                      onClick={() => openAck(key)}
                      title="Reconocer esta alarma"
                      className="flex-shrink-0 p-0.5 rounded text-gray-500 hover:text-teal-300 hover:bg-gray-700/50 transition-colors"
                    >
                      <UserCheck className="w-3 h-3" />
                    </button>
                  )}
                  {!isResolved && !fixedMasked && !mutedThisSession && (
                    <button
                      onClick={() => muteSession(canonicalId, alarm.code)}
                      title="Silenciar en esta sesión"
                      className="flex-shrink-0 p-0.5 rounded text-gray-500 hover:text-gray-300 hover:bg-gray-700/50 transition-colors"
                    >
                      <BellOff className="w-3 h-3" />
                    </button>
                  )}
                  {!isResolved && fixedMasked && (
                    <span className="flex-shrink-0 text-[9px] uppercase tracking-wide text-gray-600">enmascarada</span>
                  )}
                </div>
                {isAcking && (
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <input
                      type="text"
                      autoFocus
                      placeholder="Nota (opcional)"
                      value={ackNote}
                      onChange={e => setAckNote(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') confirmAck(canonicalId, alarm.code);
                        if (e.key === 'Escape') closeAck();
                      }}
                      maxLength={500}
                      className="flex-1 min-w-0 bg-gray-800 text-gray-100 text-[11px] px-2 py-1 rounded border border-gray-700 focus:border-teal-500 focus:outline-none"
                    />
                    <button
                      onClick={closeAck}
                      className="px-2 py-1 rounded text-[10px] font-medium bg-gray-800 text-gray-300 hover:bg-gray-700"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={() => confirmAck(canonicalId, alarm.code)}
                      disabled={ackSaving}
                      className="px-2 py-1 rounded text-[10px] font-medium bg-teal-700 text-white hover:bg-teal-600 disabled:opacity-50 disabled:cursor-wait"
                    >
                      {ackSaving ? 'Guardando…' : 'Reconocer'}
                    </button>
                  </div>
                )}
                {isAcking && ackError && (
                  <div className="mt-1 text-[10px] text-red-400">{ackError}</div>
                )}
              </div>
            );
          })
        )}
      </div>
    </FloatingPanel>
  );
}

/** Returns alarm count per driver (only active alarms, for CabinetMarker badges) */
export function useAlarmCounts(alarms: MockAlarm[]): Record<number, number> {
  const counts: Record<number, number> = {};
  for (const a of alarms) {
    if (isActiveAlarm(a)) counts[a.driverId] = (counts[a.driverId] || 0) + 1;
  }
  return counts;
}
